import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeftIcon,
  UserIcon,
  ChatBubbleLeftIcon,
  ChatBubbleLeftRightIcon,
  ClockIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  createdAt: string;
  tokenCount?: number;
}

interface ConversationDetail {
  id: string;
  user: {
    id: string;
    email: string;
    username: string;
    name: string;
  };
  companion: {
    id: string;
    name: string;
    personality: string;
    gender: string;
    avatarKey?: string;
  };
  messages: Message[];
  messageCount: number;
  totalTokensUsed: number;
  createdAt: string;
  updatedAt: string;
}

const AdminConversationDetail: React.FC = () => {
  const [conversation, setConversation] = useState<ConversationDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { conversationId } = useParams();

  useEffect(() => {
    if (conversationId) {
      fetchConversationDetails();
    }
  }, [conversationId]);

  const fetchConversationDetails = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`/admin/conversations/${conversationId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setConversation(response.data);
    } catch (err: any) {
      if (err.response?.status === 403) {
        setError('Admin access required');
        navigate('/login');
      } else if (err.response?.status === 404) {
        setError('Conversation not found');
      } else {
        setError('Failed to load conversation details');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
  };

  const calculateEstimatedCost = (tokens: number) => {
    // Rough estimate: $0.002 per 1K tokens for GPT-3.5-turbo
    const costPer1KTokens = 0.002;
    return ((tokens / 1000) * costPer1KTokens).toFixed(4);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 p-4">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-600">Loading conversation details...</div>
        </div>
      </div>
    );
  }

  if (error || !conversation) {
    return (
      <div className="min-h-screen bg-gray-100 p-4">
        <div className="flex items-center justify-center h-64">
          <div className="text-red-600">{error || 'Conversation not found'}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/admin/conversations')}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <ArrowLeftIcon className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Conversation Details</h1>
                <p className="text-sm text-gray-600 mt-1">ID: {conversation.id}</p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-sm text-gray-600">
                Started: {formatTimestamp(conversation.createdAt)}
              </div>
              <div className="text-sm text-gray-600">
                Last Activity: {formatTimestamp(conversation.updatedAt)}
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar with Details */}
          <div className="lg:col-span-1 space-y-4">
            {/* User Info */}
            <div className="bg-white shadow rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-900 mb-3 flex items-center gap-2">
                <UserIcon className="w-4 h-4 text-gray-400" />
                User
              </h3>
              <div className="space-y-2">
                <div>
                  <div className="text-sm font-medium text-gray-900">
                    {conversation.user.username || conversation.user.name || 'Unknown'}
                  </div>
                  <div className="text-xs text-gray-500">{conversation.user.email}</div>
                </div>
                <button
                  onClick={() => navigate(`/admin/users/${conversation.user.id}`)}
                  className="text-xs text-blue-600 hover:text-blue-800"
                >
                  View User Profile →
                </button>
              </div>
            </div>

            {/* Companion Info */}
            <div className="bg-white shadow rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-900 mb-3 flex items-center gap-2">
                <UserIcon className="w-4 h-4 text-gray-400" />
                Companion
              </h3>
              <div className="space-y-2">
                <div>
                  <div className="text-sm font-medium text-gray-900">
                    {conversation.companion.name}
                  </div>
                  <div className="text-xs text-gray-500">{conversation.companion.personality}</div>
                  <div className="text-xs text-gray-500">
                    Gender: {conversation.companion.gender}
                  </div>
                </div>
              </div>
            </div>

            {/* Statistics */}
            <div className="bg-white shadow rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-900 mb-3 flex items-center gap-2">
                <ChartBarIcon className="w-4 h-4 text-gray-400" />
                Statistics
              </h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-xs text-gray-600">Messages:</span>
                  <span className="text-xs font-medium text-gray-900">
                    {conversation.messageCount}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-xs text-gray-600">Tokens Used:</span>
                  <span className="text-xs font-medium text-gray-900">
                    {conversation.totalTokensUsed?.toLocaleString() || 0}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-xs text-gray-600">Est. Cost:</span>
                  <span className="text-xs font-medium text-green-600">
                    ${calculateEstimatedCost(conversation.totalTokensUsed || 0)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-xs text-gray-600">Duration:</span>
                  <span className="text-xs font-medium text-gray-900">
                    {Math.round(
                      (new Date(conversation.updatedAt).getTime() -
                        new Date(conversation.createdAt).getTime()) /
                        60000,
                    )}{' '}
                    min
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Messages */}
          <div className="lg:col-span-3">
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">
                Conversation History ({conversation.messages?.length || 0} messages)
              </h2>
              <div className="space-y-4 max-h-[800px] overflow-y-auto">
                {conversation.messages && conversation.messages.length > 0 ? (
                  conversation.messages.map((message) => (
                    <div
                      key={message.id}
                      className={`flex gap-3 ${
                        message.role === 'user' ? 'justify-end' : 'justify-start'
                      }`}
                    >
                      <div
                        className={`flex gap-3 max-w-[80%] ${
                          message.role === 'user' ? 'flex-row-reverse' : 'flex-row'
                        }`}
                      >
                        {/* Avatar */}
                        <div
                          className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                            message.role === 'user'
                              ? 'bg-blue-500 text-white'
                              : 'bg-purple-500 text-white'
                          }`}
                        >
                          {message.role === 'user' ? (
                            <UserIcon className="w-4 h-4" />
                          ) : (
                            <ChatBubbleLeftIcon className="w-4 h-4" />
                          )}
                        </div>

                        {/* Message Content */}
                        <div
                          className={`flex flex-col ${
                            message.role === 'user' ? 'items-end' : 'items-start'
                          }`}
                        >
                          <div
                            className={`rounded-lg px-4 py-2 ${
                              message.role === 'user'
                                ? 'bg-blue-500 text-white'
                                : 'bg-gray-100 text-gray-900'
                            }`}
                          >
                            <p className="text-sm whitespace-pre-wrap break-words">
                              {message.content}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs text-gray-500">
                              <ClockIcon className="inline w-3 h-3 mr-1" />
                              {formatTimestamp(message.createdAt)}
                            </span>
                            {message.tokenCount && (
                              <span className="text-xs text-gray-500">
                                • {message.tokenCount} tokens
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 text-center py-8">No messages in this conversation</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminConversationDetail;
